import { createContext, lazy, Suspense, useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, Navigate, NavLink, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { CookieBanner } from "@/components/Common";
import { useNoindex } from "@/lib/seoHead";
import { openCookieSettings } from "@/lib/consent";
import { LogoMark } from "@/components/ThemePreview";
import { api } from "@/lib/api";
import { getOnce } from "@/lib/sitePrefetch";
import { S } from "@/lib/i18n";
import { CURRENCIES, layoutOf, THEMES, THEME_KEYS, themeStyle } from "@/lib/site";

const SiteHome = lazy(() => import("./SiteHome"));
const Listings = lazy(() => import("./Listings"));
const PropertyDetail = lazy(() => import("./PropertyDetail"));
const pagesM = () => import("./Pages");
const P = Object.fromEntries(["About", "Team", "Contact", "Blog", "BlogPost", "Valuation", "Favourites", "SiteLegal"].map((k) => [k, lazy(() => pagesM().then((m) => ({ default: m[k] })))]));

const Ctx = createContext(null);
export const useSite = () => useContext(Ctx);

function useCurrency() {
  const [currency, setCur] = useState(() => localStorage.getItem("inm_currency") || "EUR");
  const [rates, setRates] = useState({ EUR: 1 });
  useEffect(() => { api.get("/public/currency").then((r) => setRates(r.data.rates)).catch(() => {}); }, []);
  const setCurrency = (c) => { localStorage.setItem("inm_currency", c); setCur(c); };
  return { currency, setCurrency, rates };
}

function Header({ transparent }) {
  const { site, lang, base, t, currency, setCurrency } = useSite();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const lay = layoutOf(site.branding);
  useEffect(() => setOpen(false), [loc.pathname]);
  const links = [["properties", t.properties], ["about", t.about], ["team", t.team], ["blog", t.blog], ["valuation", t.valuation], ["contact", t.contact]];
  const navigate = useNavigate();
  const langs = site.languages || ["en", "es"];
  const switchTo = (l) => navigate(loc.pathname.replace(`${base}/${lang}`, `${base}/${l}`) + loc.search);
  const over = transparent && lay.overHome;
  const cls = over ? "bg-gradient-to-b from-black/60 to-transparent text-white" : {
    glass: "glass border-b border-black/5",
    dark: "backdrop-blur-xl border-b s-line",
    paper: "border-b s-line bg-[var(--s-bg)]",
    white: "bg-white/90 backdrop-blur-xl border-b s-line",
    solid: "border-b-2 border-[color:var(--s-ink)] bg-[var(--s-bg)]",
  }[lay.header];
  const hdrStyle = !over && lay.header === "dark" ? { background: "color-mix(in srgb, var(--s-bg) 92%, transparent)" } : undefined;
  const navCls = ({ isActive }) => `transition-opacity hover:opacity-100 ${isActive ? "opacity-100" : "opacity-70"} ${lay.nav === "upper" ? "uppercase tracking-[0.18em] text-[11px]" : lay.nav === "wide" ? "text-[13px] tracking-wide" : "text-sm"}`;
  return (
    <header className={`${over ? "absolute" : "sticky"} inset-x-0 top-0 z-50 ${cls}`} style={hdrStyle} data-testid="site-header">
      <div className={`mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 sm:px-8 ${lay.nav === "wide" ? "h-20" : "h-16"}`}>
        <Link to={`${base}/${lang}`} className="min-w-0 shrink truncate" data-testid="site-logo"><LogoMark agency={site} className={over || lay.dark ? "h-8 max-w-[170px]" : "h-10 max-w-[180px]"} onDark={over || lay.dark} /></Link>
        <nav className="hidden items-center gap-6 lg:flex">{links.map(([k, l]) => <NavLink key={k} to={`${base}/${lang}/${k}`} className={navCls} data-testid={`site-nav-${k}`}>{l}</NavLink>)}</nav>
        <div className="flex shrink-0 items-center gap-2">
          <NavLink to={`${base}/${lang}/favourites`} className={(a) => `hidden sm:inline ${navCls(a)}`} data-testid="site-nav-favourites">{t.favourites}</NavLink>
          <select aria-label="Currency" value={currency} onChange={(e) => setCurrency(e.target.value)} className="hidden !rounded-full !border-current/20 bg-transparent px-2 py-1 text-xs sm:block" style={{ color: "inherit" }} data-testid="currency-switcher">
            {CURRENCIES.map((c) => <option key={c} value={c} className="text-black">{c}</option>)}
          </select>
          {langs.length > 1 && <select aria-label={t.language} value={lang} onChange={(e) => switchTo(e.target.value)} className="!rounded-full !border-current/20 bg-transparent px-2 py-1 text-xs font-semibold uppercase" style={{ color: "inherit" }} data-testid="site-lang-switcher">
            {langs.map((l) => <option key={l} value={l} className="text-black">{l.toUpperCase()}</option>)}
          </select>}
          <button className="lg:hidden" onClick={() => setOpen(true)} aria-label="Open menu" data-testid="site-menu-btn"><Menu className="h-5 w-5" /></button>
        </div>
      </div>
      {createPortal(<AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="site fixed inset-0 z-[1100] flex flex-col overflow-y-auto p-6" style={{ ...themeStyle(site.branding), background: "var(--s-bg)" }} data-testid="site-mobile-menu">
            <button className="self-end" onClick={() => setOpen(false)} aria-label="Close menu" data-testid="site-menu-close"><X className="h-6 w-6" /></button>
            <nav className="mt-8 flex flex-col gap-5">{[...links, ["favourites", t.favourites]].map(([k, l]) => <Link key={k} to={`${base}/${lang}/${k}`} className="s-head text-3xl" data-testid={`site-mobile-nav-${k}`}>{l}</Link>)}</nav>
            <select aria-label="Currency" value={currency} onChange={(e) => setCurrency(e.target.value)} className="mt-8 w-40 px-3 py-2 text-sm" data-testid="mobile-currency-switcher">{CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}</select>
            <div className="mt-6 flex flex-wrap gap-2" data-testid="mobile-lang-list">{langs.map((l) => <button key={l} onClick={() => switchTo(l)} className={`px-3 py-1.5 text-sm uppercase ${l === lang ? "s-btn" : "s-btn-ghost"}`} data-testid={`mobile-lang-${l}`}>{l}</button>)}</div>
          </motion.div>
        )}
      </AnimatePresence>, document.body)}
    </header>
  );
}

function Footer() {
  const { site, lang, base, t } = useSite();
  const year = new Date().getFullYear();
  return (
    <footer className="mt-24 border-t s-line s-surface" data-testid="site-footer">
      <div className="mx-auto grid max-w-7xl gap-10 px-5 py-14 sm:grid-cols-3 sm:px-8">
        <div><LogoMark agency={site} className="h-9" onDark={layoutOf(site.branding).dark} /><p className="mt-4 max-w-xs text-sm s-muted">{site.tagline?.[lang]}</p></div>
        <div className="text-sm"><p className="s-head font-semibold">{t.contact}</p><p className="mt-3 s-muted">{site.contact?.address}</p><p className="s-muted">{site.contact?.phone}</p><p className="s-muted">{site.contact?.email}</p></div>
        <div className="flex flex-col gap-2 text-sm">
          <Link to={`${base}/${lang}/privacy`} className="s-muted hover:underline" data-testid="site-footer-privacy">{t.privacy}</Link>
          <Link to={`${base}/${lang}/terms`} className="s-muted hover:underline" data-testid="site-footer-terms">{t.terms}</Link>
          <Link to={`${base}/${lang}/privacy#cookies`} className="s-muted hover:underline" data-testid="site-footer-cookies">{t.cookiePolicy}</Link>
          <Link to={`${base}/${lang}/valuation`} className="s-muted hover:underline">{t.valuation}</Link>
          <button type="button" onClick={openCookieSettings} className="s-muted text-left hover:underline" data-testid="site-footer-cookie-settings">{t.cookieSettings || "Cookies"}</button>
        </div>
      </div>
      <div className="mx-auto flex max-w-7xl flex-wrap justify-between gap-2 px-5 pb-8 text-xs s-muted sm:px-8"><span>© {year} {site.name}. {t.rights}</span>{!site.remove_branding && <a href="/" className="hover:underline">{t.poweredBy}</a>}</div>
    </footer>
  );
}

function Shell({ children }) {
  const { site, t, lang, base } = useSite();
  const loc = useLocation();
  const isHome = loc.pathname.replace(/\/+$/, "") === `${base}/${lang}`;
  const lay = layoutOf(site.branding);
  return (
    <div className="site min-h-screen overflow-x-hidden" style={themeStyle(site.branding)} data-theme={site.branding.theme} data-testid={`agency-site-${site.branding.theme}`}>
      {site.is_demo && <DemoBar />}
      {site.preview && <PreviewBar />}
      <div className="relative">
      <Header transparent={isHome} />
      <AnimatePresence mode="wait">
        <motion.main key={loc.pathname} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
          <Suspense fallback={<div className="min-h-[70vh]" />}>{children}</Suspense>
        </motion.main>
      </AnimatePresence>
      <Footer />
      </div>
      <CookieBanner storageKey={`inm_cookie_${site.slug}`} text={t.cookies} accept={t.accept} reject={t.reject} privacyHref={`${base}/${lang}/privacy#cookies`} privacyLabel={t.cookiePolicy} dark={lay.dark} />
    </div>
  );
}

// Free preview: unpublished draft site. Visible banner + noindex (the static host has no SSR, so set robots client-side too).
const PREVIEW_NOTE = {
  en: "Preview — this website isn't published yet.", es: "Vista previa: esta web aún no está publicada.",
  de: "Vorschau – diese Website ist noch nicht veröffentlicht.", nl: "Voorbeeld – deze website is nog niet gepubliceerd.",
  fr: "Aperçu – ce site n'est pas encore publié.", sv: "Förhandsvisning – webbplatsen är inte publicerad än.", no: "Forhåndsvisning – nettstedet er ikke publisert ennå.",
};
function PreviewBar() {
  const { lang } = useSite();
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots"; m.content = "noindex, nofollow"; m.setAttribute("data-preview", "1");
    document.head.appendChild(m);
    return () => m.remove();
  }, []);
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-[#0B1A2E] px-3 py-2 text-center text-xs text-white" data-testid="site-preview-banner" role="status">
      <span className="rounded-full bg-[#C8A96A] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#0B1A2E]">{lang === "es" ? "Vista previa" : "Preview"}</span>
      <span>{PREVIEW_NOTE[lang] || PREVIEW_NOTE.en}</span>
      {window.location.pathname.startsWith("/s/") && (
        <a href="/app/billing" className="rounded-full bg-[#0EA5A0] px-3 py-0.5 font-semibold text-white hover:bg-[#0D948F]" data-testid="site-preview-choose-plan">{lang === "es" ? "¿Es tu agencia? Elige un plan para publicar" : "Your agency? Choose a plan to publish"}</a>
      )}
    </div>
  );
}

// Demo sites only: try any template via ?theme=<key> (kept for the browser tab) or the switcher in the demo bar.
const demoThemeKey = (slug) => `inm_demo_theme_${slug}`;
function DemoBar() {
  const { site, t, lang } = useSite();
  const pick = (k) => { sessionStorage.setItem(demoThemeKey(site.slug), k); window.dispatchEvent(new Event("inm-demo-theme")); };
  return (
    <div className="s-surface flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-b s-line px-3 py-1.5 text-center text-[11px] s-muted" data-testid="site-demo-note">
      <span>{t.demoNote}</span>
      <label className="inline-flex items-center gap-1.5">{lang === "es" ? "Plantilla" : "Template"}
        <select value={site.branding.theme} onChange={(e) => pick(e.target.value)} className="rounded-full border s-line bg-transparent px-2 py-0.5 text-[11px]" style={{ color: "inherit" }} data-testid="demo-theme-switcher">
          {THEME_KEYS.map((k) => <option key={k} value={k} className="text-black">{THEMES[k].name}</option>)}
        </select>
      </label>
    </div>
  );
}

function useDemoTheme(site, slug) {
  const [key, setKey] = useState(() => {
    const q = new URLSearchParams(window.location.search).get("theme");
    if (q && THEMES[q]) { sessionStorage.setItem(demoThemeKey(slug), q); return q; }
    return sessionStorage.getItem(demoThemeKey(slug));
  });
  useEffect(() => {
    const on = () => setKey(sessionStorage.getItem(demoThemeKey(slug)));
    window.addEventListener("inm-demo-theme", on);
    return () => window.removeEventListener("inm-demo-theme", on);
  }, [slug]);
  return useMemo(() => {
    if (!site?.is_demo || !key || !THEMES[key] || key === site.branding?.theme) return site;
    return { ...site, branding: { ...site.branding, theme: key, primary_color: "", accent_color: "" } };
  }, [site, key]);
}

function SiteNotFound() {
  const { site, base, lang, t } = useSite();
  useNoindex();
  useEffect(() => { document.title = site?.name ? `${t.notFoundPage} · ${site.name}` : t.notFoundPage; }, [t, site?.name]);
  return (
    <section className="mx-auto max-w-3xl px-5 py-24 text-center sm:px-8" data-testid="site-not-found">
      <p className="text-xs font-semibold uppercase tracking-widest s-accent">404</p>
      <h1 className="s-head mt-3 text-4xl font-semibold">{t.notFoundPage}</h1>
      <p className="mt-4 s-muted">{t.notFoundText}</p>
      <Link to={`${base}/${lang}`} className="s-btn mt-8 inline-block px-6 py-3 text-sm font-medium" data-testid="site-not-found-home">{t.backHome}</Link>
    </section>
  );
}

function LangRoutes({ site, base }) {
  const { lang } = useParams();
  const cur = useCurrency();
  const value = useMemo(() => ({ site, base, lang, t: S[lang], ...cur }), [site, base, lang, cur]);
  useEffect(() => { document.documentElement.lang = lang; }, [lang]);
  const dl = site.languages?.includes(site.default_lang) ? site.default_lang : "en";
  // Unknown/disabled language: keep the rest of the path. If the first segment isn't a language at all (e.g. /contact), keep it too so it resolves or shows the 404.
  if (!S[lang] || !(site.languages || ["en"]).includes(lang)) return <Navigate to={`${base}/${dl}${window.location.pathname.replace(new RegExp(`^${base}${S[lang] ? "/[^/]+" : ""}`), "")}`} replace />;
  return (
    <Ctx.Provider value={value}>
      <Shell>
        <Routes>
          <Route index element={<SiteHome />} />
          <Route path="properties" element={<Listings />} />
          <Route path="property/:param" element={<PropertyDetail />} />
          <Route path="about" element={<P.About />} />
          <Route path="team" element={<P.Team />} />
          <Route path="contact" element={<P.Contact />} />
          <Route path="blog" element={<P.Blog />} />
          <Route path="blog/:post" element={<P.BlogPost />} />
          <Route path="valuation" element={<P.Valuation />} />
          <Route path="favourites" element={<P.Favourites />} />
          <Route path="privacy" element={<P.SiteLegal kind="privacy" />} />
          <Route path="terms" element={<P.SiteLegal kind="terms" />} />
          <Route path="*" element={<SiteNotFound />} />
        </Routes>
      </Shell>
    </Ctx.Provider>
  );
}

function SiteLoader({ slug, base }) {
  const [site, setSite] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => { getOnce(api, `/public/sites/${slug}`).then((r) => setSite(r.data)).catch(() => setMissing(true)); }, [slug]);
  const shown = useDemoTheme(site, slug);
  useEffect(() => {  // renamed agency: /s/<old>/... forwards to /s/<new>/...
    if (site?.slug && site.slug !== slug && base.startsWith("/s/")) {
      const rest = window.location.pathname.slice(base.length);
      window.location.replace(`/s/${site.slug}${rest}${window.location.search}${window.location.hash}`);
    }
  }, [site, slug, base]);
  if (missing) return <div className="flex min-h-screen items-center justify-center text-slate-500" data-testid="site-not-found">Site not found</div>;
  if (!site) return <div className="min-h-screen" />;
  return (
    <Routes>
      <Route index element={<Navigate to={`${base}/${site.default_lang || "en"}`} replace />} />
      <Route path=":lang/*" element={<LangRoutes site={shown} base={base} />} />
    </Routes>
  );
}

export default function SiteApp() {
  const { slug } = useParams();
  return <SiteLoader slug={slug} base={`/s/${slug}`} />;
}

// Host-based resolution: {slug}.<ROOT_DOMAIN> or verified custom domain.
export function HostSite() {
  const [slug, setSlug] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    api.get("/public/resolve-host", { params: { host: window.location.hostname } }).then((r) => {
      const to = r.data.redirect;  // old subdomain -> new one, subdomain -> own domain once live, apex -> www
      if (to && /^https:\/\/[^/]+$/.test(to) && new URL(to).hostname !== window.location.hostname) {
        window.location.replace(`${to}${window.location.pathname}${window.location.search}${window.location.hash}`);
        return;
      }
      setSlug(r.data.slug);
    }).catch(() => setErr(true));
  }, []);
  if (err) return <div className="flex min-h-screen items-center justify-center text-slate-500">Site not found</div>;
  if (!slug) return <div className="min-h-screen" />;
  return <Suspense fallback={<div className="min-h-screen" />}><Routes><Route path="/*" element={<SiteLoader slug={slug} base="" />} /></Routes></Suspense>;
}
